import GestionArchivo from "../Archivo/GestionArchivo";
import PinEfectorSelect from "./PinEfectorSelect";
import api from "../../services/Api";
// src/pages/efectores/AdministrarEfectores.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import efectorService from "../../services/efectorService";
import { obtenerMisDispositivos } from "../../services/dispositivoService";
import usuarioService from "../../services/usuarioService";
import { usePermisos } from "../../context/PermisosContext";
import { isSuperAdmin } from "../../services/authUtils";

/** Pines válidos para EFECTORES (ESP32 DevKit v1) */

const toPinString = (n) => (Number.isFinite(+n) ? `D${Number(n)}` : "");
const pinToNumber = (pinStr) => {
  if (!pinStr) return "";
  const s = String(pinStr).trim().toUpperCase();
  return s.startsWith("D") ? s.slice(1) : s;
};

// Normaliza permisos a array<string>
const normalizarPermisos = (p) => {
  if (Array.isArray(p)) return p.map(String);
  if (typeof p === "string" && p.trim().length) {
    return p.split(",").map((x) => x.trim()).filter(Boolean).map(String);
  }
  return [];
};

// Convierte payloads comunes a array
const toArray = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.result)) return data.result;
  if (Array.isArray(data?.usuarios)) return data.usuarios;
  return [];
};

// Retardo
const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

const AdministrarEfectores = () => {
  const { can, canEdit } = usePermisos();

  // permisos efectivos
  const esSuper = useMemo(() => !!isSuperAdmin(), []);
  const puedeVer = esSuper || !!can?.("EFECTORES_VIEW");
  const puedeEditar = esSuper || !!canEdit?.("EFECTORES_EDIT");

  const [efectores, setEfectores] = useState([]);
  const [dispositivos, setDispositivos] = useState([]);
  const [tiposEfector, setTiposEfector] = useState([]);
  const [usuarios, setUsuarios] = useState([]);

  const [mensaje, setMensaje] = useState("");
  const [error, setError] = useState("");

  // Mensajes por tarjeta
  const [flash, setFlash] = useState({}); // { [id]: { ok?: string, err?: string } }
  const [busqueda, setBusqueda] = useState("");
  const [forzarCambioPin, setForzarCambioPin] = useState({});
  // Preserve unsaved form fields when the background status refresh runs.
  const camposPendientes = useRef(new Map());

  const efectoresPorEstanque = useMemo(() => {
    const grupos = new Map();
    const q = busqueda.trim().toLocaleLowerCase("es");
    efectores.filter((efector) => {
      const tipo = tiposEfector.find((t) => String(t.id) === String(efector.tipoEfectorId))?.nombre;
      return !q || [efector.nombre, efector.descripcion, efector.estanqueNombre, tipo,
        efector.pinActivacion, efector.encendido ? "encendido" : "apagado"]
        .some((v) => String(v ?? "").toLocaleLowerCase("es").includes(q));
    }).forEach((efector) => {
      const estanque = efector.estanqueNombre || "Sin estanque";
      if (!grupos.has(estanque)) grupos.set(estanque, []);
      grupos.get(estanque).push(efector);
    });
    return Array.from(grupos.entries()).sort(([a], [b]) => a.localeCompare(b, "es"));
  }, [efectores, tiposEfector, busqueda]);

  const showFlash = useCallback((id, { ok, err }) => {
    setFlash((prev) => ({ ...prev, [id]: { ok, err } }));
    setTimeout(() => {
      setFlash((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }, 3000);
  }, []);

  const cargarDatos = useCallback(async () => {
    if (!puedeVer) return;
    try {
      const [efectoresRaw, dispositivosData, tiposData, usuariosData] = await Promise.all([
        efectorService.obtenerTodosLosEfectores(),
        obtenerMisDispositivos(),
        efectorService.obtenerTiposEfector(),
        esSuper
          ? usuarioService.listarPermisosTemporales()
          : usuarioService.obtenerUsuariosCreadosPorAdmin(),
      ]);

      // Aplana Estanque -> Dispositivo -> Efectores
      const efectoresPlanos = (efectoresRaw || [])
        .flatMap((e) =>
          (e.dispositivos || []).map((d) => ({
            EstanqueNombre: e.estanqueNombre,
            DispositivoId: d.dispositivoId,
            DispositivoNombre: d.dispositivoNombre,
            Efectores: d.efectores || [],
          }))
        )
        .flatMap((x) =>
          x.Efectores.map((f) => ({
            id: f.id,
            nombre: f.nombre,
            descripcion: f.descripcion || "",
            tipoEfectorId: f.tipoEfectorId,
            dispositivoId: x.DispositivoId,
            pinActivacion: f.pinActivacion, // "D22"
            estaActivo: !!f.estaActivo,
            encendido: !!f.encendido,
            estadoConfirmado: typeof f.estadoConfirmado === "boolean" ? f.estadoConfirmado : null,
            comandoVersion: Number(f.comandoVersion || 0),
            fechaConfirmacionUtc: f.fechaConfirmacionUtc || null,
            ultimoErrorDispositivo: f.ultimoErrorDispositivo || "",
            permisos: normalizarPermisos(f.permisos),
            estanqueNombre: x.EstanqueNombre,
            dispositivoNombre: x.DispositivoNombre,
          }))
        );

      setEfectores((actuales) => efectoresPlanos.map((remoto) => {
        const local = actuales.find((item) => item.id === remoto.id);
        const campos = camposPendientes.current.get(remoto.id);
        if (!local || !campos) return remoto;
        const conservar = {};
        for (const campo of campos) {
          const localValor = campo === "permisos" ? normalizarPermisos(local[campo]).sort() : local[campo];
          const remotoValor = campo === "permisos" ? normalizarPermisos(remoto[campo]).sort() : remoto[campo];
          if (JSON.stringify(localValor) === JSON.stringify(remotoValor)) campos.delete(campo);
          else conservar[campo] = local[campo];
        }
        if (!campos.size) camposPendientes.current.delete(remoto.id);
        return { ...remoto, ...conservar };
      }));
      setDispositivos(toArray(dispositivosData));
      setTiposEfector(toArray(tiposData));
      setUsuarios(toArray(usuariosData));
      setError("");
    } catch (err) {
      console.error("Error cargando datos:", err);
      setError("Error al cargar datos.");
    }
  }, [esSuper, puedeVer]);

  useEffect(() => {
    cargarDatos();
    const timer = setInterval(cargarDatos, 5000);
    return () => clearInterval(timer);
  }, [cargarDatos]);

  const handleChange = useCallback((id, campo, valor) => {
    const pendientes = camposPendientes.current.get(id) || new Set();
    pendientes.add(campo);
    camposPendientes.current.set(id, pendientes);
    setEfectores((prev) =>
      prev.map((e) =>
        e.id === id
          ? { ...e, [campo]: campo.includes("Id") ? Number(valor) : valor }
          : e
      )
    );
  }, []);

  const handleChangePin = useCallback((id, gpioStr) => {
    const pinStr = toPinString(gpioStr);
    handleChange(id, "pinActivacion", pinStr);
  }, [handleChange]);

  const togglePermiso = useCallback((efectorId, usuarioId) => {
    if (!puedeEditar) return;
    const pendientes = camposPendientes.current.get(efectorId) || new Set();
    pendientes.add("permisos");
    camposPendientes.current.set(efectorId, pendientes);
    const uid = String(usuarioId);
    setEfectores((prev) =>
      prev.map((e) =>
        e.id === efectorId
          ? {
              ...e,
              permisos: normalizarPermisos(e.permisos).includes(uid)
                ? normalizarPermisos(e.permisos).filter((x) => x !== uid)
                : [...normalizarPermisos(e.permisos), uid],
            }
          : e
      )
    );
  }, [puedeEditar]);

  const actualizarEfector = useCallback(async (efector) => {
    if (!puedeEditar) return;
    try {
      if (forzarCambioPin[efector.id] && !window.confirm("Si el GPIO elegido está ocupado, el recurso que lo usa quedará archivado. ¿Deseas forzar el cambio?")) return;
      if (!/^D\d+$/.test(efector.pinActivacion || "")) {
        showFlash(efector.id, { err: "El pin debe tener el formato D seguido de número (ej. D22)." });
        setError("El pin debe tener el formato D seguido de número (ej. D22).");
        setMensaje("");
        return;
      }

      await efectorService.actualizarEfector(efector.id, {
        nombre: efector.nombre,
        descripcion: efector.descripcion || "",
        tipoEfectorId: Number(efector.tipoEfectorId),
        dispositivoId: Number(efector.dispositivoId),
        pinActivacion: efector.pinActivacion,
        forzarCambioPin: !!forzarCambioPin[efector.id],
        estaActivo: !!efector.estaActivo,
        permisos: normalizarPermisos(efector.permisos).map(Number),
      });

      showFlash(efector.id, { ok: "Efector actualizado correctamente." });
      setForzarCambioPin((prev) => ({ ...prev, [efector.id]: false }));
      setMensaje("Efector actualizado correctamente.");
      setError("");
    } catch (err) {
      console.error("Error actualizando efector:", err);
      const detalle = err?.response?.data;
      const mensajeError = typeof detalle === "string" ? detalle : detalle?.mensaje || "Error al actualizar el efector.";
      showFlash(efector.id, { err: mensajeError });
      setError(mensajeError);
      setMensaje("");
    }
  }, [puedeEditar, showFlash, forzarCambioPin]);

  // Enciende/Apaga: usa endpoint específico que PROPAGA y PERSISTE
  const cambiarEncendido = useCallback(async (efector, encender) => {
    if (!puedeEditar) return;

    const previo = efector.encendido;

    // Mostrar la orden inmediatamente, pero no fingir confirmación física.
    setEfectores((prev) =>
      prev.map((e) => (e.id === efector.id ? { ...e, encendido: !!encender, estadoConfirmado: null } : e))
    );

    try {
      // Retardo (3s) antes de aplicar
      await sleep(3000);

      // PUT /api/Efectores/{id}/encendido
      const resp = await efectorService.setEncendido(efector.id, {
        encendido: !!encender,
        propagarAlDispositivo: true,
      });

      const msg = resp?.dispositivoActualizado
        ? `El dispositivo ya confirmó ${encender ? "encendido" : "apagado"}.`
        : `Orden guardada. Esperando confirmación física del ESP32…`;

      showFlash(efector.id, { ok: msg });
      setMensaje(msg);
      setError("");
    } catch (e) {
      console.error("Error al cambiar encendido:", e);
      // rollback UI si falla
      setEfectores((prev) =>
        prev.map((x) => (x.id === efector.id ? { ...x, encendido: previo } : x))
      );
      showFlash(efector.id, { err: "No se pudo aplicar el estado en el dispositivo." });
      setError("No se pudo aplicar el estado en el dispositivo.");
      setMensaje("");
    }
  }, [puedeEditar, showFlash]);

  if (!puedeVer) {
    return (
      <div className="p-6 bg-neutral-950 text-neutral-300 rounded-lg max-w-4xl mx-auto">
        No tienes permisos para ver Efectores.
      </div>
    );
  }

  return (
    <div className="p-6 bg-neutral-900 text-white rounded-lg shadow-md">
      <GestionArchivo tipo="efectores" puedeEditar={puedeEditar} onChanged={cargarDatos} />
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-green-400 text-2xl font-semibold">Administrar Efectores</h3>
        {esSuper && (
          <span className="text-xs border border-emerald-500/60 px-2 py-1 rounded text-emerald-300">
            Superadministrador
          </span>
        )}
      </div>

      {efectores.length === 0 && !error && (
        <p className="text-center text-cyan-300">No hay efectores para mostrar.</p>
      )}

      <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
        placeholder="Buscar por efector, tipo, estanque, pin o estado…"
        className="w-full mb-4 p-2.5 rounded bg-neutral-800 border border-cyan-700/40 text-white" />

      {efectoresPorEstanque.map(([estanqueNombre, efectoresGrupo]) => (
        <section key={estanqueNombre} className="mb-7">
          <h4 className="text-green-400 text-xl font-semibold mb-3 border-b border-emerald-700/40 pb-2">
            {estanqueNombre}
          </h4>
      {efectoresGrupo.map((efector) => {
        const pinNumberValue = pinToNumber(efector.pinActivacion || "");
        const permisosStr = normalizarPermisos(efector.permisos);
        const tipoNombre = tiposEfector.find((tipo) => String(tipo.id) === String(efector.tipoEfectorId))?.nombre || "Sin tipo";
        const estadoVisible = efector.estadoConfirmado;
        const ordenPendiente = efector.estadoConfirmado !== efector.encendido;

        return (
          <details key={efector.id} className="border border-cyan-700/40 rounded mb-3 bg-neutral-800 overflow-hidden">
            <summary className="cursor-pointer select-none px-4 py-3 hover:bg-neutral-700/70 transition-colors list-none">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="font-semibold text-cyan-200">{efector.nombre || "Sin nombre"}</span>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-neutral-300">
                    {tipoNombre} <span className="text-neutral-500 mx-2">•</span> {estanqueNombre}
                  </span>
                  <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${
                    estadoVisible
                      ? "border-emerald-500/70 bg-emerald-950/60 text-emerald-300"
                      : "border-neutral-500/70 bg-neutral-950/60 text-neutral-300"
                  }`}>
                    {estadoVisible == null ? "SIN CONFIRMACIÓN" : estadoVisible ? "ENCENDIDO" : "APAGADO"}
                  </span>
                  {ordenPendiente && (
                    <span className="rounded-full border border-amber-500/70 bg-amber-950/60 px-2.5 py-1 text-xs font-semibold text-amber-300">
                      ORDEN PENDIENTE
                    </span>
                  )}
                </div>
              </div>
            </summary>
            <div className="p-4 border-t border-cyan-700/30">
            <h4 className="text-cyan-300 text-lg mb-2">
              Estanque: <span className="text-cyan-200">{efector.estanqueNombre || "No asignado"}</span>
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-1">Nombre</label>
                <input
                  value={efector.nombre}
                  onChange={(e) => handleChange(efector.id, "nombre", e.target.value)}
                  className="w-full p-2 rounded bg-neutral-700 text-white"
                  disabled={!puedeEditar}
                />
              </div>

              <div>
                <label className="block mb-1">Descripción</label>
                <input
                  value={efector.descripcion || ""}
                  onChange={(e) => handleChange(efector.id, "descripcion", e.target.value)}
                  className="w-full p-2 rounded bg-neutral-700 text-white"
                  disabled={!puedeEditar}
                />
              </div>

              <div>
                <label className="block mb-1">Tipo de Efector</label>
                <select
                  value={efector.tipoEfectorId || ""}
                  onChange={(e) => handleChange(efector.id, "tipoEfectorId", e.target.value)}
                  className="w-full p-2 rounded bg-neutral-700 text-white"
                  disabled={!puedeEditar}
                >
                  <option value="">Seleccione</option>
                  {(Array.isArray(tiposEfector) ? tiposEfector : []).map((tp) => (
                    <option key={tp.id} value={tp.id}>
                      {tp.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block mb-1">Dispositivo</label>
                <select
                  value={efector.dispositivoId || ""}
                  onChange={(e) => handleChange(efector.id, "dispositivoId", e.target.value)}
                  className="w-full p-2 rounded bg-neutral-700 text-white"
                  disabled={!puedeEditar}
                >
                  <option value="">Seleccione</option>
                  {(Array.isArray(dispositivos) ? dispositivos : []).map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.nombre}
                    </option>
                  ))}
                </select>
              </div>

              {/* Selector de pin */}
              <div>
                <label className="block mb-1">Pin de Activación</label>
                <PinEfectorSelect dispositivoId={efector.dispositivoId} efectorId={efector.id}
                  value={pinNumberValue}
                  onChange={(e) => handleChangePin(efector.id, e.target.value)} disabled={!puedeEditar} allowOccupied={!!forzarCambioPin[efector.id]} className="w-full p-2 rounded bg-neutral-700 text-white" />
                <label className="mt-2 flex items-center gap-2 text-sm text-amber-200">
                  <input type="checkbox" checked={!!forzarCambioPin[efector.id]} onChange={(e) => setForzarCambioPin((prev) => ({ ...prev, [efector.id]: e.target.checked }))} disabled={!puedeEditar} />
                  Forzar cambio de pin: archivar el sensor o efector que ocupa el GPIO
                </label>
                <p className="text-xs text-neutral-400 mt-1">
                  Evita GPIO 34–39 (solo entrada), 6–11 (flash) y 1/3 (UART0). Los pines reservados no se ofrecen en este perfil.
                </p>
              </div>

              <div>
                <label className="block mb-1">Activo</label>
                <select
                  value={efector.estaActivo ? "true" : "false"}
                  onChange={(e) => handleChange(efector.id, "estaActivo", e.target.value === "true")}
                  className="w-full p-2 rounded bg-neutral-700 text-white"
                  disabled={!puedeEditar}
                >
                  <option value="true">Activo</option>
                  <option value="false">Inactivo</option>
                </select>
              </div>

              {/* Orden deseada y estado físico confirmado son datos distintos. */}
              <div className="md:col-span-2">
                <div className={`text-sm ${efector.encendido ? "text-green-300" : "text-red-300"}`}>
                  Orden solicitada: {efector.encendido ? "ENCENDER" : "APAGAR"}
                </div>
                <div className={`text-sm mt-1 ${efector.estadoConfirmado === efector.encendido ? "text-cyan-300" : "text-amber-300"}`}>
                  <p>Orden enviada: {efector.encendido ? "ENCENDER" : "APAGAR"}. El funcionamiento físico requiere comprobación presencial.</p>
                  <button type="button" disabled={!puedeEditar} className="bg-neutral-700 px-3 py-1 rounded" onClick={async () => {
                    if (!window.confirm('¿Cableado comprobado y equipo seguro para activar hasta 10 segundos? La prueba queda pendiente de entrega hasta 60 segundos.')) return;
                    try {const {data}=await api.post(`/Efectores/${efector.id}/prueba-fisica`, {confirmarEquipoSeguro:true}); alert(data.mensaje);} catch(err) {alert(typeof err.response?.data === 'string' ? err.response.data : 'No se pudo iniciar la prueba.');}
                  }}>Prueba física de 10 segundos</button>
                  Estado reportado por ESP32: {efector.estadoConfirmado === null
                    ? "SIN CONFIRMACIÓN"
                    : efector.estadoConfirmado ? "ENCENDIDO" : "APAGADO"}
                  {efector.estadoConfirmado !== efector.encendido ? " · ORDEN PENDIENTE" : " · SALIDA REPORTADA"}
                </div>
                {efector.ultimoErrorDispositivo && <div className="text-xs text-red-300 mt-1">{efector.ultimoErrorDispositivo}</div>}
              </div>
            </div>

            <div className="mt-4">
              <label className="block mb-2">Permisos</label>
              <div className="grid grid-cols-1 md:grid-cols-2 max-h-32 overflow-y-auto bg-neutral-900 p-2 rounded">
                {(Array.isArray(usuarios) ? usuarios : []).map((usuario) => {
                  const uid = String(usuario.id);
                  const checked = permisosStr.includes(uid);
                  return (
                    <label key={usuario.id} className={`flex items-center text-sm ${!puedeEditar ? "opacity-60" : ""}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => togglePermiso(efector.id, usuario.id)}
                        className="mr-2"
                        disabled={!puedeEditar}
                      />
                      {usuario.nombre} <span className="text-neutral-400">({usuario.email})</span>
                    </label>
                  );
                })}
                {(Array.isArray(usuarios) ? usuarios : []).length === 0 && (
                  <div className="text-neutral-400 text-sm">Sin usuarios disponibles.</div>
                )}
              </div>
            </div>

            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <button
                onClick={() => actualizarEfector(efector)}
                className={`px-4 py-2 rounded ${
                  puedeEditar ? "bg-green-600 hover:bg-green-700 text-white" : "bg-neutral-700 text-neutral-300 cursor-not-allowed"
                }`}
                disabled={!puedeEditar}
              >
                Guardar cambios
              </button>

              {/* Encender/Apagar */}
              <button
                onClick={() => cambiarEncendido(efector, true)}
                className={`px-4 py-2 rounded ${
                  puedeEditar ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-neutral-700 text-neutral-300 cursor-not-allowed"
                }`}
                disabled={!puedeEditar}
              >
                Encender
              </button>
              <button
                onClick={() => cambiarEncendido(efector, false)}
                className={`px-4 py-2 rounded ${
                  puedeEditar ? "bg-red-600 hover:bg-red-700 text-white" : "bg-neutral-700 text-neutral-300 cursor-not-allowed"
                }`}
                disabled={!puedeEditar}
              >
                Apagar
              </button>

              {/* Mensajes por tarjeta */}
              {flash[efector.id]?.ok && <span className="text-green-400 text-sm">{flash[efector.id].ok}</span>}
              {flash[efector.id]?.err && <span className="text-red-400 text-sm">{flash[efector.id].err}</span>}
            </div>
            </div>
          </details>
        );
      })}
        </section>
      ))}

      {mensaje && <p className="text-green-400 mt-4">{mensaje}</p>}
      {error && <p className="text-red-400 mt-4">{error}</p>}
    </div>
  );
};

export default AdministrarEfectores;
